import type { QueryClient } from '@tanstack/react-query';
import type { Estimate } from '@/types/estimate';

const SIGNED_MEMBERSHIP_ESTIMATE_ERROR =
  'The estimate was approved, but its signed PDF could not be prepared. Please contact support before retrying.';

/**
 * A signature approval that accepts a membership cannot use the pre-approval
 * estimate for its document of record: the server may have changed line
 * prices, tax, totals and the payment schedule. Refuse to report success when
 * neither the approval response nor the mandatory post-approval read produced
 * that authoritative estimate.
 */
export function requireApprovedEstimateForSignedMembership<E>(
  estimate: E | null,
  required: boolean,
): asserts estimate is E {
  if (required && !estimate) {
    throw new Error(SIGNED_MEMBERSHIP_ESTIMATE_ERROR);
  }
}

/**
 * Prefix every cached slice of the public quote payload hangs off.
 *
 * Deliberately excludes the settlement-token suffix the route appends to its
 * own key, so writes and refetches here match regardless of which variant is
 * mounted. Shared with `refetchPublicEstimate` so the two cannot drift — a
 * mismatch between them would silently miss rather than error.
 */
export function publicEstimateQueryPrefix(id: string) {
  return ['public', 'estimate', id] as const;
}

/**
 * Merge the customer-approval response into the cached public estimate.
 *
 * WHY THIS EXISTS: approval reprices when the customer changed an optional
 * selection, moving `depositAmount` and therefore the card surcharge. The
 * deposit CTA renders immediately afterwards, and the Stripe session is created
 * server-side from the NEW stored amount — so a screen still showing the old
 * one discloses a fee that is not the fee charged.
 *
 * The follow-up GET cannot be relied on to correct this. TanStack Query
 * swallows query errors unless `throwOnError` is set, so a failed refetch
 * RESOLVES and leaves the previous payload in cache — indistinguishable from
 * success at the call site. Seeding from the approval response, which is
 * authoritative and already in hand, is what makes the post-approval screen
 * correct rather than best-effort.
 *
 * A MERGE, not a replace: the approval response is the bare estimate row, while
 * the GET enriches it (rendered notes, tags, latest deposit payment status).
 * Spreading the response over the cached entry keeps that enrichment and lets
 * absent keys fall through to what is already there.
 */
export function seedApprovedPublicEstimate<E extends object, S>(
  queryClient: Pick<QueryClient, 'setQueriesData'>,
  id: string,
  approved:
    | {
        estimate?: Partial<E>;
        depositSurcharge?: S;
        depositCollectedSummary?: Estimate['depositCollectedSummary'];
      }
    | null
    | undefined,
  queryKey: readonly unknown[] = publicEstimateQueryPrefix(id),
): void {
  const patch = approved?.estimate;
  if (!patch) return;
  const surcharge = approved?.depositSurcharge;

  queryClient.setQueriesData<{
    estimate: E;
    depositSurcharge?: S;
    depositCollectedSummary?: Estimate['depositCollectedSummary'];
  }>({ queryKey }, (old) => {
    // The same prefix also matches the sibling 'images' / 'payment-plan' /
    // 'pdf-download-url' entries. Only touch the one carrying an estimate.
    if (!old || typeof old !== 'object' || !('estimate' in old)) return old;
    return {
      ...old,
      estimate: { ...old.estimate, ...patch },
      // `??`, not `||`: a surcharge block is an object when present and
      // absent otherwise, and keeping the cached one on absence is right —
      // the server omitting it is not a signal that surcharging stopped.
      depositSurcharge: surcharge ?? old.depositSurcharge,
      ...(approved?.depositCollectedSummary !== undefined
        ? { depositCollectedSummary: approved.depositCollectedSummary }
        : {}),
    };
  });
}
